import { App } from "../App";
import { RING_MATERIALS, LIMITED_MATERIALS } from "../Model/RingMaterials";
import { RING_SURFACES } from "../Model/RingSurfaces";
import React from "react";
import styled from "styled-components";


interface Props {
  app:App;
}

interface State {
  cameraId:string;
  imageSize:number;
  inProgress:boolean;
}

const Canvas = styled.canvas<{ imageSize: number }>
`
  display: block;
  border: 2px solid black;
  background-color: white;
  width: ${props => props.imageSize}px;
  height: ${props => props.imageSize}px;
`;

/*
  Recursively goes through all material & surface combinations and downloads a screenshot of each one.
  Quirks:
    - needs to run on a horizontal screen ("landscape", "wider than high")
    - tab needs to be kept open (so WebGL keeps doing things properly)
*/

export class MaterialImageCapturer extends React.Component<Props, State> {
  private _canvas = React.createRef<HTMLCanvasElement>();

  constructor(props) {
    super(props);

    this.state = {
      cameraId: 'id_0.66786503029089',
      imageSize: 512,
      inProgress: false
    };
  };

  public componentDidMount = () => {
    window.dispatchEvent(new Event('resize'));
  }// /()

  public captureMaterialImages = () => {
    console.log("MaterialImageCapturer.captureMaterialImages() called!");

    this._initializeCanvas()
    .then(() => {
      const materialList:{material:string, surface:string}[] = [];
      RING_MATERIALS.forEach((ringMaterial) => {
        RING_SURFACES.forEach((ringSurface) => {
          materialList.push({
            material: ringMaterial.name,
            surface: ringSurface
          })
        });
      });

      this.props.app.state.renderEngine.configurator.setCamera(this.state.cameraId, { time: 0 })
      .then(() => {
        //setCamera seems to resolve before it is actually done, so lets wait a little longer just to be safe
        window.setTimeout(() => {
          handleMaterialListEntryRecursive(materialList);
        }, 3000)
      });
    });

    const handleMaterialListEntryRecursive = (materialList:{material:string, surface:string}[]) => {
      if (materialList.length <= 0) return;
      const currentListEntry = materialList[0];
      let renderEngineName = `${currentListEntry.material} ${currentListEntry.surface} MATCAP`;
      let screenshotName = `${currentListEntry.material} ${currentListEntry.surface}`;

      if (LIMITED_MATERIALS.includes(currentListEntry.material)) {
        renderEngineName = `${currentListEntry.material} MATCAP`;
        screenshotName = `${currentListEntry.material} ${currentListEntry.surface}`;  //We take screenshots with each surface even for limited materials to avoid having to add additional logic to our menu
      };
      
      this.props.app.state.renderEngine.changeMaterial('ImageCaptureSphere_Zone_01', renderEngineName);

      window.setTimeout(() => {
        this._getSingleScreenshot()
        .then((screenshotBlob) => {
          this._downloadScreenshotBlob(screenshotBlob, screenshotName);

          materialList.shift();
          handleMaterialListEntryRecursive(materialList);
        })
      }, 3000)
    };// /const handleMaterialListEntryRecursive()
  };// /captureMaterialImages()

  private _initializeCanvas = ():Promise<void> => {
    return new Promise((resolve, reject) => {
      this.setState({
        inProgress: true
      }, () => {
        this._canvas.current.width = this.state.imageSize;
        this._canvas.current.height = this.state.imageSize;
        resolve();
      });
    });
  };// /_initializeCanvas()

  private _getSingleScreenshot = ():Promise<Blob> => {
    const canvasContext = this._canvas.current.getContext('2d');

    return new Promise((resolve, reject) => {
      this.props.app.state.renderEngine.getScreenShot({})
      .then((screenshotBlob) => {
        const image = new Image();
        image.src = URL.createObjectURL(screenshotBlob);
        image.addEventListener('load', (e) => {
          canvasContext.drawImage(
            image,
            (image.width - image.height)/2,   //sx
            0,                                //sy
            image.height,                     //sWidth
            image.height,                     //sHeight
            0,                                //dx
            0,                                //dy
            this.state.imageSize,             //dWidth
            this.state.imageSize,             //dHeight
          )
          this._canvas.current.toBlob((blob) => {
            resolve(blob)
          }, 'image/png', 1);
        });
      });
    });
  };// /_getSingleScreenshot()

  private _downloadScreenshotBlob = (screenshotBlob:Blob, name:string) => {
    const image = new Image();
    image.src = URL.createObjectURL(screenshotBlob);
    const link = document.createElement('a');
    link.href = image.src;
    link.target = '_blank';
    link.download = `${name}.png`;
    link.click();
  };// /_downloadScreenshotBlob()

  public render = () => {
    return(
      <div>
        <Canvas
          ref={this._canvas}
          imageSize={this.state.imageSize}
        />
        {!this.state.inProgress && 
          <button onClick={this.captureMaterialImages}>
            START
          </button>
        }
      </div>
    );
  };// /render()
};// /class MaterialImageCapturer